/**
 * VinlessCleanupCard (Jul-2026, User-Wunsch) — Admin System-Bereinigung:
 * Fahrzeuge OHNE Fahrgestellnummer (VIN) löschen. Vorschau je Händler,
 * nur VERKAUFTE Fahrzeuge werden server-seitig übersprungen (reservierte ohne FIN werden mitgelöscht).
 */
import { useState } from "react";
import { Trash2, Loader2, ScanBarcode, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

export const VinlessCleanupCard = () => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [checked, setChecked] = useState({});

    const loadPreview = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/vinless-vehicles");
            setPreview(data);
            const init = {};
            (data.groups || []).forEach((g) => { init[g.owner_id] = true; });
            setChecked(init);
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
        finally { setLoading(false); }
    };

    const run = async () => {
        const owners = (preview?.groups || []).filter((g) => checked[g.owner_id]);
        const total = owners.reduce((s, g) => s + g.count, 0);
        if (!total) return;
        if (!window.confirm(`${total} Fahrzeug(e) ohne VIN endgültig löschen?\n\nNur verkaufte Fahrzeuge werden übersprungen — reservierte ohne FIN werden mitgelöscht. Die Aktion kann nicht rückgängig gemacht werden.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/vinless-vehicles/delete", { owner_ids: owners.map((g) => g.owner_id) });
            toast.success(`${data.deleted} Fahrzeug(e) ohne VIN gelöscht`);
            setPreview(null);
        } catch (e) { toast.error(e.response?.data?.detail || "Bereinigung fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <div className="bg-white border-2 border-amber-300 rounded-sm p-4" data-testid="vinless-cleanup-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <ScanBarcode className="w-5 h-5 text-amber-600" /> Fahrzeuge ohne VIN löschen
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        Nur verkaufte Fahrzeuge sind geschützt — reservierte ohne FIN werden mitgelöscht (Reservierung wird gelöst).
                    </p>
                </div>
                <Button onClick={loadPreview} disabled={loading} variant="outline" className="rounded-sm border-gray-300" data-testid="vinless-preview-btn">
                    {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Vorschau laden
                </Button>
            </div>
            {preview && (
                <div className="mt-3 space-y-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                        {preview.total} Fahrzeuge ohne VIN löschbar
                        {preview.skipped_sold_or_reserved ? ` · ${preview.skipped_sold_or_reserved} verkaufte geschützt` : ""}
                    </div>
                    <ul className="text-xs text-gray-700 space-y-0.5" data-testid="vinless-groups">
                        {(preview.groups || []).map((g) => (
                            <li key={g.owner_id} className="flex items-center gap-2 border-b border-gray-100 py-1">
                                <input type="checkbox" checked={!!checked[g.owner_id]}
                                    onChange={(e) => setChecked((s) => ({ ...s, [g.owner_id]: e.target.checked }))}
                                    data-testid={`vinless-owner-${g.owner_id}`} />
                                <span className="truncate flex-1">
                                    {g.company_name || "—"} <span className="text-gray-400">({g.email})</span>
                                    {(g.samples || []).length > 0 && (
                                        <span className="block text-[10px] text-gray-400 truncate" data-testid={`vinless-samples-${g.owner_id}`}>
                                            z.B. {g.samples.join(" · ")}
                                        </span>
                                    )}
                                </span>
                                <span className="font-mono-num font-bold shrink-0">{g.count}</span>
                            </li>
                        ))}
                        {(preview.groups || []).length === 0 && <li className="text-gray-400">Keine Fahrzeuge ohne VIN vorhanden.</li>}
                    </ul>
                    <Button onClick={run} disabled={busy || !preview.total || Object.values(checked).every((v) => !v)}
                        className="bg-red-700 hover:bg-red-800 text-white rounded-sm font-bold" data-testid="vinless-run-btn">
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                        Endgültig löschen
                    </Button>
                </div>
            )}
        </div>
    );
};
